import React from 'react';
import classnames from 'classnames';
import { findIndex as _findIndex } from 'lodash';
import moment from 'moment';
import isURL from 'validator/lib/isURL';

import ExecutiveReportModal from '@containers/admin/Agency/ExecutiveReportModal';

import Button, { EditButton } from '@components/Button';
import Input from '@components/Input';
import ModalBase from '@components/ModalBase';
import Table from '@components/Table';

import { getEmptyErrorState, randomCodeGenerator } from '@utils/AppUtil';
import CyberdyneValidator from '@utils/cyberdyneValidator';
import ErrorMessageGenerator from '@utils/ErrorMessageGenerator';
import { generateForwardEmail } from '@utils/forwardEmailUtil';

export interface IProductCRMFormProps {
  show: boolean;
  crm?: ICRMResponse;
  onClose: (e: React.SyntheticEvent<HTMLElement>) => void;
  onSubmit: (CRMRequest: ICRMRequest) => void;
  agencyName?: string;
  agencyId?: number;
}

interface IProductCRMFormState {
  crm: ICRMResponse;
  error: {
    name: IFieldValidation;
    CSNPrefix: IFieldValidation;
    CSNInitialization: IFieldValidation;
    faqUrl: IFieldValidation;
    importEmailId: IFieldValidation;
    citizenPortalHomeLinkURL: IFieldValidation;
    citizenPortalHomeLinkText: IFieldValidation;
    appleAuth0ConnectionName: IFieldValidation;
  };
  isExecutiveReportModalOpen: boolean;
  selectedExecutiveReport: IConfigExecutiveReportResponse;
  isEdit: boolean;
}

const cyberdyneValidator = new CyberdyneValidator();
const errorMsgGenerator = new ErrorMessageGenerator();

class ProductCRMForm extends React.Component<IProductCRMFormProps, IProductCRMFormState> {
  readonly productName = 'CRM';

  constructor(props: IProductCRMFormProps) {
    super(props);

    this.state = this.getEmptyState();
  }

  componentDidUpdate(prevProps: IProductCRMFormProps) {
    if (prevProps.show !== this.props.show && this.props.show) {
      // When opening the form, check if email needs regeneration
      const crmData = {
        ...this.getEmptyState().crm,
        ...this.props.crm,
      };

      // Always use email from props if available (for existing CRM)
      // This ensures email is displayed even if it was previously empty in state
      if (this.props.crm?.importEmailId) {
        crmData.importEmailId = this.props.crm.importEmailId;
      }

      this.setState({
        crm: crmData,
        error: {
          name: getEmptyErrorState(),
          CSNInitialization: getEmptyErrorState(),
          CSNPrefix: getEmptyErrorState(),
          faqUrl: getEmptyErrorState(),
          importEmailId: getEmptyErrorState(),
          citizenPortalHomeLinkText: getEmptyErrorState(),
          citizenPortalHomeLinkURL: getEmptyErrorState(),
          appleAuth0ConnectionName: getEmptyErrorState(),
        },
      });
      if (!!this.props.crm) {
        this.setState({
          isEdit: true,
        });
      } else {
        this.setState({
          isEdit: false,
        });
      }
    }
  }

  getEmptyState = (): IProductCRMFormState => ({
    crm: {
      name: '',
      isActive: true,
      token: '',
      CSNPrefix: '',
      CSNInitialization: 1,
      isCSNResettable: true,
      isCSNYearIncluded: true,
      isFAQEnabled: false,
      commlinkStatus: CommlinkStatus.OFF,
      faqUrl: '',
      importEmailEnabled: false,
      importEmailId: '',
      executiveReports: [],
      citizenPortalHomeLinkText: 'Back to Citizen Portal Home',
      citizenPortalHomeLinkURL: null,
      isMobileAppEnabled: false,
      isPhotoFirstIntakeEnabled: false,
      isCategoryClassificationEnabled: false,
      isStaffTextIntakeEnabled: false,
      appleAuth0ConnectionName: null,
    },
    error: {
      name: getEmptyErrorState(),
      CSNInitialization: getEmptyErrorState(),
      CSNPrefix: getEmptyErrorState(),
      faqUrl: getEmptyErrorState(),
      importEmailId: getEmptyErrorState(),
      citizenPortalHomeLinkText: getEmptyErrorState(),
      citizenPortalHomeLinkURL: getEmptyErrorState(),
      appleAuth0ConnectionName: getEmptyErrorState(),
    },
    isExecutiveReportModalOpen: false,
    selectedExecutiveReport: null,
    isEdit: false,
  });

  onChange = (e?: React.SyntheticEvent<HTMLInputElement>) => {
    const name = e.currentTarget.name;
    const inputType = e.currentTarget.type;
    const value = inputType === 'number' ? parseInt(e.currentTarget.value, 10) : e.currentTarget.value;

    this.setState((prevState) => ({
      ...prevState,
      crm: {
        ...prevState.crm,
        [name]: value,
      },
    }));
  };

  validateForm = () => {
    let isValid = true;
    const { crm } = this.state;
    const { error } = this.getEmptyState();

    if (cyberdyneValidator.isEmptyValue(crm.name)) {
      error.name = {
        errorState: IValidationState.ERROR,
        errorMessage: 'Product name cannot be empty',
      };

      isValid = false;
    } else if (cyberdyneValidator.isStringGreaterThanLength(crm.name.trim(), 50)) {
      error.name = {
        errorState: IValidationState.ERROR,
        errorMessage: 'Product name cannot be longer than 50 characters',
      };

      isValid = false;
    }

    if (cyberdyneValidator.isEmptyValue(crm.CSNInitialization)) {
      error.CSNInitialization.errorState = IValidationState.ERROR;
      error.CSNInitialization.errorMessage = 'Number Initialization is a mandatory field';

      isValid = false;
    } else if (!cyberdyneValidator.isNumberInRange(crm.CSNInitialization, 0, 99999999)) {
      error.CSNInitialization.errorState = IValidationState.ERROR;
      error.CSNInitialization.errorMessage = 'Number Initialization can be atmost 8 characters long';

      isValid = false;
    }

    if (crm.CSNPrefix && !cyberdyneValidator.isValidNumberingPrefix(crm.CSNPrefix)) {
      error.CSNPrefix.errorState = IValidationState.ERROR;
      error.CSNPrefix.errorMessage = 'Invalid Number Prefix';

      isValid = false;
    } else if (crm.CSNPrefix && cyberdyneValidator.isStringGreaterThanLength(crm.CSNPrefix.trim(), 5)) {
      error.CSNPrefix.errorState = IValidationState.ERROR;
      const errMsg = errorMsgGenerator.getCharacterLimitMessage('CSN Prefix', 5);
      error.CSNPrefix.errorMessage = errMsg;

      isValid = false;
    }

    if (crm.isFAQEnabled && (!crm.faqUrl || !cyberdyneValidator.isStringLengthLimited(crm.faqUrl.trim(), 1, 500))) {
      error.faqUrl.errorState = IValidationState.ERROR;
      const errMsg = errorMsgGenerator.getMinMaxCharacterLimitMessage('URL', 1, 500);
      error.faqUrl.errorMessage = errMsg;

      isValid = false;
    } else if (crm.isFAQEnabled && crm.faqUrl && !isURL(crm.faqUrl)) {
      error.faqUrl.errorState = IValidationState.ERROR;
      error.faqUrl.errorMessage = 'Enter valid URL.';
      isValid = false;
    }

    if (
      !crm.citizenPortalHomeLinkText ||
      !cyberdyneValidator.isStringLengthLimited(crm.citizenPortalHomeLinkText.trim(), 1, 60)
    ) {
      error.citizenPortalHomeLinkText = {
        errorState: IValidationState.ERROR,
        errorMessage: errorMsgGenerator.getMinMaxCharacterLimitMessage('URL Text', 1, 60),
      };

      isValid = false;
    }

    if (
      !crm.citizenPortalHomeLinkURL ||
      !cyberdyneValidator.isStringLengthLimited(crm.citizenPortalHomeLinkURL.trim(), 1, 120)
    ) {
      error.citizenPortalHomeLinkURL.errorState = IValidationState.ERROR;
      const errMsg = errorMsgGenerator.getMinMaxCharacterLimitMessage('URL', 1, 120);
      error.citizenPortalHomeLinkURL.errorMessage = errMsg;

      isValid = false;
    } else if (crm.citizenPortalHomeLinkURL && !isURL(crm.citizenPortalHomeLinkURL)) {
      error.citizenPortalHomeLinkURL.errorState = IValidationState.ERROR;
      error.citizenPortalHomeLinkURL.errorMessage = 'Enter valid URL.';
      isValid = false;
    }

    if (crm.isMobileAppEnabled && !crm.appleAuth0ConnectionName?.trim()) {
      error.appleAuth0ConnectionName.errorState = IValidationState.ERROR;
      error.appleAuth0ConnectionName.errorMessage = 'Apple Auth0 Connection Name is required when Mobile App is enabled';

      isValid = false;
    }

    this.setState({ error });

    return isValid;
  };

  onSubmit = (e: React.SyntheticEvent<HTMLElement>) => {
    if (this.validateForm()) {
      const token = randomCodeGenerator(10);
      const { crm } = this.state;

      let requestData: ICRMRequest = {
        ...crm,
        name: crm.name.trim(),
        ...(crm.token ? {} : { token }),
        CSNPrefix: (crm.CSNPrefix || '').trim(),
        faqUrl: crm.isFAQEnabled ? crm.faqUrl.trim() : null,
        citizenPortalHomeLinkURL: crm.citizenPortalHomeLinkURL,
        citizenPortalHomeLinkText: crm.citizenPortalHomeLinkText,
      };

      if (crm.isMobileAppEnabled) {
        requestData.appleAuth0ConnectionName = crm.appleAuth0ConnectionName.trim();
      } else {
        const { appleAuth0ConnectionName, ...withoutAppleConnection } = requestData;
        requestData = withoutAppleConnection as ICRMRequest;
      }

      // Do not send importEmailId when it is not enabled or empty.
      // Backend owns generation/clearing of this field and expects either
      // a non-empty string or a missing/null value.
      if (!crm.importEmailEnabled || !crm.importEmailId) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { importEmailId, ...withoutEmail } = requestData;
        requestData = withoutEmail as ICRMRequest;
      }

      this.props.onSubmit(requestData);
    }
  };

  onFaqChange = (e?: React.SyntheticEvent<any>) => {
    const value = e.currentTarget.value;
    this.setState((prevState) => ({
      ...prevState,
      crm: {
        ...prevState.crm,
        isFAQEnabled: value,
      },
    }));
  };

  onCommlinkChange = (e?: React.SyntheticEvent<any>) => {
    const value = e.currentTarget.value;
    this.setState((prevState) => ({
      ...prevState,
      crm: {
        ...prevState.crm,
        commlinkStatus: value,
      },
    }));
  };

  onMobileAppChange = (e?: React.SyntheticEvent<any>) => {
    const value = e.currentTarget.value;
    this.setState((prevState) => ({
      ...prevState,
      crm: {
        ...prevState.crm,
        isMobileAppEnabled: value,
        appleAuth0ConnectionName: value ? prevState.crm.appleAuth0ConnectionName : null,
      },
    }));
  };

  onPhotoFirstIntakeChange = (e?: React.SyntheticEvent<any>) => {
    const value = e.currentTarget.value;
    this.setState((prevState) => ({
      ...prevState,
      crm: {
        ...prevState.crm,
        isPhotoFirstIntakeEnabled: value,
      },
    }));
  };

  onCategoryClassificationChange = (e?: React.SyntheticEvent<any>) => {
    const value = e.currentTarget.value;
    this.setState((prevState) => ({
      ...prevState,
      crm: {
        ...prevState.crm,
        isCategoryClassificationEnabled: value,
      },
    }));
  };

  onStaffTextIntakeChange = (e?: React.SyntheticEvent<any>) => {
    const value = e.currentTarget.value;
    this.setState((prevState) => ({
      ...prevState,
      crm: {
        ...prevState.crm,
        isStaffTextIntakeEnabled: value,
      },
    }));
  };

  onImportEmailChange = (e?: React.SyntheticEvent<any>) => {
    const value = e.currentTarget.value;
    this.setState((prevState) => {
      // Get email from props first (for existing CRM), then from state
      const existingEmail = this.props.crm?.importEmailId || prevState.crm.importEmailId || '';

      let importEmailId = '';

      if (value) {
        if (existingEmail) {
          // Use existing email if available (e.g. after migration)
          importEmailId = existingEmail;
        } else {
          // Generate email optimistically on the client for better UX
          // Backend will still validate and/or regenerate as needed
          const agencyId = this.props.agencyId || this.props.crm?.agencyId;

          if (agencyId) {
            try {
              importEmailId = generateForwardEmail(agencyId);
            } catch (error) {
              // If generation fails for any reason, leave it empty.
              // Server-side logic in edit/create will still generate a valid email.
              // eslint-disable-next-line no-console
              console.error('Failed to generate Forward-In email on toggle:', error);
              importEmailId = '';
            }
          }
        }
      }

      return {
        ...prevState,
        crm: {
          ...prevState.crm,
          importEmailEnabled: value,
          // If enabling, use existing/momentarily generated email; if disabling, clear it
          importEmailId: value ? importEmailId : '',
        },
      };
    });
  };

  renderCSN = () => {
    const twoStateOptions = [
      { value: true, label: 'Yes' },
      { value: false, label: 'No' },
    ];

    const {
      crm: { isCSNYearIncluded, CSNPrefix, isCSNResettable, CSNInitialization },
      error,
    } = this.state;
    const CSNDisplayYear = isCSNYearIncluded ? `${moment().format('YY')}-` : '';
    const CSNDisplayPrefix = CSNPrefix ? `${CSNPrefix}-` : '';
    const CSNDisplayInitialization = CSNInitialization ? `${CSNInitialization}` : '';
    const CSNDisplay = `${CSNDisplayPrefix}` + `${CSNDisplayYear}` + `${CSNDisplayInitialization}`;
    const disclaimerText = `Customer Submission Number Initialization
      will be reset on the Mid-Night of every New Year.`;

    return (
      <div className="product-form__case-numbering">
        <div className="product-form__case-numbering-settings">
          <div className="product-form__case-numbering-settings-fields">
            <Input
              field={{
                label: 'Prefix',
                type: CustomFieldType.TEXT,
                isRequired: false,
                value: CSNPrefix || '',
              }}
              error={error.CSNPrefix}
              width={12}
              name="CSNPrefix"
              onChange={this.onChange}
            />
            <Input
              width={12}
              field={{
                label: 'Include Year?',
                type: OtherFieldType.RADIOBUTTON,
                value: isCSNYearIncluded,
                isRequired: true,
              }}
              name="isCSNYearIncluded"
              valueLabelOptions={twoStateOptions}
              onChange={this.onChange}
            />
            <Input
              field={{
                label: 'Number Initialization',
                type: CustomFieldType.NUMBER,
                isRequired: true,
                value: CSNInitialization,
              }}
              error={error.CSNInitialization}
              width={12}
              name="CSNInitialization"
              onChange={this.onChange}
            />
            {isCSNYearIncluded && (
              <Input
                width={12}
                field={{
                  label: 'Reset Numbers Each Year?',
                  type: OtherFieldType.RADIOBUTTON,
                  value: isCSNResettable,
                  isRequired: false,
                }}
                name="isCSNResettable"
                valueLabelOptions={twoStateOptions}
                onChange={this.onChange}
              />
            )}
          </div>
          {isCSNYearIncluded && (
            <div className="product-form__disclaimer">
              <i />
              <label>{disclaimerText}</label>
            </div>
          )}
        </div>
        <div className="product-form__case-numbering-display">
          <div className="product-form__secondary-header">Submission Number Display:</div>
          <label>{CSNDisplay}</label>
        </div>
      </div>
    );
  };

  renderExecutiveReportsSettings = () => {
    const columns = [
      { accessor: 'label', Header: 'Dashboard name', sortable: false },
      { accessor: 'dashboardId', Header: 'Dashboard ID', sortable: false },
      {
        accessor: 'isActive',
        Header: 'Status',
        sortable: false,
        Cell: (cell) => (cell.value ? 'Active' : 'Inactive'),
      },
      {
        accessor: '',
        Header: '',
        Cell: (cell) => (
          <EditButton
            onClick={this.toggleExecutiveReportSetting(cell.original)}
            className={classnames('space-right')}
          />
        ),
        sortable: false,
        width: 100,
      },
    ];

    const { executiveReports: rows } = this.state.crm;

    return (
      <div className="product-form__executive-reports">
        <div>
          <Table noDataText={'No Dashboard Configured'} columns={columns} rows={rows} plainTable={true} />
        </div>
        {this.renderNewExecutiveReportActionButton()}
        <ExecutiveReportModal
          show={this.state.isExecutiveReportModalOpen}
          executiveReport={this.state.selectedExecutiveReport}
          allReports={rows}
          onClose={this.toggleExecutiveReportSetting()}
          onSubmit={this.onSaveExecutiveReport}
          isCRM={ConfigProductType.CRM}
        />
      </div>
    );
  };

  toggleExecutiveReportSetting =
    (executiveReport: IConfigExecutiveReportResponse = null) =>
    (e: React.SyntheticEvent<HTMLElement>) => {
      const selectedExecutiveReport = executiveReport || null;

      this.setState((prevState) => ({
        isExecutiveReportModalOpen: !prevState.isExecutiveReportModalOpen,
        selectedExecutiveReport,
      }));
    };

  renderNewExecutiveReportActionButton = () => (
    <div onClick={this.toggleExecutiveReportSetting(null)} className="product-form__add-report">
      <i />
      Add Report
    </div>
  );

  onSaveExecutiveReport = (executiveReport: IConfigExecutiveReportRequest) => {
    this.toggleExecutiveReportSetting(null)(null);
    const allReports = [...this.state.crm.executiveReports];
    if (executiveReport.id && allReports.length > 0) {
      const editReportIdx = _findIndex(allReports, { id: executiveReport.id });
      allReports.splice(editReportIdx, 1, executiveReport);
    } else if (executiveReport.tempId) {
      const editReportIdx = _findIndex(allReports, {
        tempId: executiveReport.tempId,
      });
      if (editReportIdx > -1) {
        allReports.splice(editReportIdx, 1, executiveReport);
      } else {
        allReports.push(executiveReport);
      }
    }
    this.setState((prevState) => ({
      crm: {
        ...prevState.crm,
        executiveReports: [...allReports],
      },
    }));
  };

  renderTitle = () => (this.props.crm ? `Edit & Configure ${this.productName}` : `Add & Configure ${this.productName}`);

  renderBody = () => {
    const statusOptions = [
      { value: true, label: 'Active' },
      { value: false, label: 'Inactive' },
    ];

    const toggleOptions = [
      { value: true, label: 'ON' },
      { value: false, label: 'OFF' },
    ];

    const commLinktoggleOptions = [
      { value: CommlinkStatus.OFF, label: 'OFF' },
      { value: CommlinkStatus.ENTERPRISE, label: 'ON' },
    ];
    const { crm, error } = this.state;

    return (
      <div className={classnames('product-form__body')}>
        <div className={classnames('product-form__info', 'crm')}>
          <Input
            field={{
              label: 'Product Name',
              type: CustomFieldType.TEXT,
              isRequired: true,
              value: crm.name || '',
            }}
            error={error.name}
            width={12}
            name="name"
            onChange={this.onChange}
          />
          <Input
            width={12}
            field={{
              label: 'Status',
              type: OtherFieldType.RADIOBUTTON,
              value: crm.isActive === undefined ? true : crm.isActive,
              isRequired: true,
            }}
            valueLabelOptions={statusOptions}
            name="isActive"
            onChange={this.onChange}
          />
        </div>
        <div className="product-form__primary-header">Customer Submission Numbering</div>
        {this.renderCSN()}
        <div className="crm-faq-managment-block col-md-12 col-xs-12">
          <div className="product-form__primary-header">Customer Submission List - Citizen View</div>
          <div className={classnames('col-md-6', 'col-xs-6')}>
            <Input
              field={{
                label: 'Back to Home Link URL',
                type: CustomFieldType.TEXT,
                isRequired: true,
                value: crm.citizenPortalHomeLinkURL,
              }}
              error={error.citizenPortalHomeLinkURL}
              width={12}
              name="citizenPortalHomeLinkURL"
              onChange={this.onChange}
            />
            <Input
              field={{
                label: 'Back to Home Link Text',
                type: CustomFieldType.TEXT,
                isRequired: true,
                value: crm.citizenPortalHomeLinkText,
              }}
              error={error.citizenPortalHomeLinkText}
              width={12}
              name="citizenPortalHomeLinkText"
              onChange={this.onChange}
            />
          </div>
        </div>
        <div className="crm-faq-managment-block col-md-12 col-xs-12">
          <div className={classnames('col-md-6', 'col-xs-6')}>
            <Input
              field={{
                label: 'FAQ Management',
                type: OtherFieldType.RADIOBUTTON,
                isRequired: false,
                value: crm.isFAQEnabled,
              }}
              width={12}
              valueLabelOptions={toggleOptions}
              name="isFaqEnabled"
              onChange={this.onFaqChange}
            />
            {crm.isFAQEnabled && (
              <Input
                field={{
                  label: 'URL',
                  type: CustomFieldType.TEXT,
                  isRequired: crm.isFAQEnabled,
                  value: crm.faqUrl,
                }}
                error={error.faqUrl}
                width={12}
                name="faqUrl"
                onChange={this.onChange}
              />
            )}
          </div>
        </div>
        <div className="crm-import-email-block col-md-12 col-xs-12">
          <div className={classnames('col-md-6', 'col-xs-6')}>
            <Input
              field={{
                label: 'Import Email',
                type: OtherFieldType.RADIOBUTTON,
                isRequired: false,
                value: crm.importEmailEnabled,
              }}
              width={12}
              valueLabelOptions={toggleOptions}
              name="importEmailEnabled"
              onChange={this.onImportEmailChange}
            />
            {crm.importEmailEnabled && (
              <div>
                <Input
                  field={{
                    label: 'Import Email',
                    type: CustomFieldType.TEXT,
                    isRequired: crm.importEmailEnabled,
                    value: crm.importEmailId || '',
                  }}
                  error={error.importEmailId}
                  width={12}
                  name="importEmailId"
                  disabled={true}
                  onChange={this.onChange}
                />
              </div>
            )}
          </div>
        </div>
        <div className="crm-commlink-block col-md-12 col-xs-12">
          <div className={classnames('col-md-6', 'col-xs-6')}>
            <Input
              field={{
                label: 'CommLink Enterprise',
                type: OtherFieldType.RADIOBUTTON,
                isRequired: false,
                value: crm.commlinkStatus,
              }}
              width={12}
              valueLabelOptions={commLinktoggleOptions}
              name="commlinkStatus"
              onChange={this.onCommlinkChange}
            />
          </div>
        </div>
        <div className="crm-mobile-app-block col-md-12 col-xs-12">
          <div className={classnames('col-md-6', 'col-xs-6')}>
            <Input
              field={{
                label: 'Mobile App',
                type: OtherFieldType.RADIOBUTTON,
                isRequired: false,
                value: crm.isMobileAppEnabled ?? false,
              }}
              width={12}
              valueLabelOptions={toggleOptions}
              name="isMobileAppEnabled"
              onChange={this.onMobileAppChange}
            />
            {crm.isMobileAppEnabled && (
              <Input
                field={{
                  label: 'Apple Auth0 Connection Name',
                  type: CustomFieldType.TEXT,
                  isRequired: true,
                  value: crm.appleAuth0ConnectionName || '',
                }}
                error={error.appleAuth0ConnectionName}
                width={12}
                name="appleAuth0ConnectionName"
                onChange={this.onChange}
              />
            )}
            {crm.isMobileAppEnabled && (
              <Input
                field={{
                  label: 'AI Photo-First Intake',
                  type: OtherFieldType.RADIOBUTTON,
                  isRequired: false,
                  value: crm.isPhotoFirstIntakeEnabled ?? false,
                }}
                width={12}
                valueLabelOptions={[
                  { value: true, label: 'Yes' },
                  { value: false, label: 'No' },
                ]}
                name="isPhotoFirstIntakeEnabled"
                onChange={this.onPhotoFirstIntakeChange}
              />
            )}
          </div>
        </div>
        <div className="crm-category-classification-block col-md-12 col-xs-12">
          <div className={classnames('col-md-6', 'col-xs-6')}>
            <Input
              field={{
                label: 'AI Category Classification',
                type: OtherFieldType.RADIOBUTTON,
                isRequired: false,
                value: crm.isCategoryClassificationEnabled ?? false,
              }}
              width={12}
              valueLabelOptions={[
                { value: true, label: 'Yes' },
                { value: false, label: 'No' },
              ]}
              name="isCategoryClassificationEnabled"
              onChange={this.onCategoryClassificationChange}
            />
          </div>
        </div>
        <div className="crm-staff-text-intake-block col-md-12 col-xs-12">
          <div className={classnames('col-md-6', 'col-xs-6')}>
            <Input
              field={{
                label: 'AI Staff Text Intake',
                type: OtherFieldType.RADIOBUTTON,
                isRequired: false,
                value: crm.isStaffTextIntakeEnabled ?? false,
              }}
              width={12}
              valueLabelOptions={[
                { value: true, label: 'Yes' },
                { value: false, label: 'No' },
              ]}
              name="isStaffTextIntakeEnabled"
              onChange={this.onStaffTextIntakeChange}
            />
          </div>
        </div>
        {!!crm.id && this.state.isEdit && (
          <div className={classnames('crm-executive-report-block')}>
            <div className="product-form__primary-header">QS Reports</div>
            {this.renderExecutiveReportsSettings()}
          </div>
        )}
      </div>
    );
  };

  renderFooter = () => {
    const submitBtnTxt = this.props.crm ? 'Save' : `Add ${this.productName}`;

    return (
      <div className={classnames('product-form__footer')}>
        <Button bsStyle={ButtonStyle.DEFAULT} onClick={this.props.onClose} content="Cancel" />
        <Button bsStyle={ButtonStyle.PRIMARY} onClick={this.onSubmit} content={submitBtnTxt} />
      </div>
    );
  };

  render() {
    return (
      <ModalBase
        show={this.props.show}
        onClose={this.props.onClose}
        titleElement={this.renderTitle()}
        bodyElement={this.renderBody()}
        footerElement={this.renderFooter()}
        className={classnames('product-form')}
      />
    );
  }
}

export default ProductCRMForm;
